import type { SceneOrigin } from '@/types/sceneTypes';
import type { PublishFlowMetadata } from '@/components/publish-flow/writePublishFlowMetadata';
import { joinDescription, splitDescription } from '@/modules/library/utils/storyAttributionUtils';
import { sanitizeLocationText } from '@/utils/locationUtils';

// A field is absent when nothing changed it
export type StoryPublishMetadata = Pick<PublishFlowMetadata, 'storyName' | 'sceneIntroduction' | 'address' | 'latitude' | 'longitude' | 'city'>;

// Built the way writePublishFlowMetadata builds it
export function withStoryMetadata(origin: SceneOrigin, meta: StoryPublishMetadata): SceneOrigin {
  const next: SceneOrigin = { ...origin };
  if (meta.storyName !== undefined) next.sceneOriginName = meta.storyName;
  if (meta.sceneIntroduction !== undefined) {
    const { attributions } = splitDescription(origin.sceneOriginDescription || '');
    next.sceneOriginDescription = joinDescription(splitDescription(meta.sceneIntroduction).userText, attributions);
  }
  if (meta.address !== undefined || meta.latitude !== undefined || meta.longitude !== undefined) {
    const lat = parseFloat(meta.latitude ?? '');
    const lng = parseFloat(meta.longitude ?? '');
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    const city = sanitizeLocationText(meta.city !== undefined ? meta.city : origin.sceneOriginLocationInfo?.city) || undefined;
    const address = sanitizeLocationText(meta.address);
    next.sceneOriginLocationInfo = hasCoords || address
      ? ({
          ...(city ? { city } : {}),
          address: address || null,
          ...(hasCoords ? { coordinates: { lat, lng } } : {}),
        } as SceneOrigin['sceneOriginLocationInfo'])
      : null;
  }
  return next;
}
